import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';
import { C } from '../styles';
import { FONT } from '../constants';

interface Props {
  linesCount: number;
  finalizing: boolean;
  savingDraft: boolean;
  submitBlocked: boolean;
  onBack: () => void;
  onPreview: () => void;
  onSubmit: () => void;
  onSaveForLater: () => void;
}

export default function SubmitFooter({
  linesCount, finalizing, savingDraft, submitBlocked,
  onBack, onPreview, onSubmit, onSaveForLater,
}: Props) {
  const [submitMenuOpen, setSubmitMenuOpen] = useState<boolean>(false);
  const submitMenuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!submitMenuOpen) return;
    const onDocClick = (e: MouseEvent): void => {
      if (submitMenuRef.current && !submitMenuRef.current.contains(e.target as Node)) {
        setSubmitMenuOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') setSubmitMenuOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [submitMenuOpen]);

  const disabled = finalizing || submitBlocked || linesCount === 0;

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      position: 'fixed', bottom: 0, left: 0, right: 0,
      background: C.card, padding: '16px 32px',
      borderTop: '1px solid ' + C.line,
      boxShadow: '0 -4px 12px rgba(0,0,0,0.06)',
      zIndex: 100,
    }}>
      <button
        onClick={onBack}
        style={{
          padding: '12px 18px', background: C.card,
          border: '1.5px solid ' + C.ink, borderRadius: 10,
          color: C.ink, fontSize: 14, fontWeight: 700,
          cursor: 'pointer', fontFamily: FONT,
        }}
      >
        {'\u2190'} Back to hours
      </button>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <button
          onClick={onPreview}
          disabled={linesCount === 0}
          style={{
            padding: '12px 20px', background: C.card,
            border: '1.5px solid ' + C.ink, borderRadius: 10,
            color: C.ink, fontSize: 14, fontWeight: 600,
            cursor: linesCount === 0 ? 'not-allowed' : 'pointer',
            fontFamily: FONT,
            opacity: linesCount === 0 ? 0.45 : 1,
          }}
        >
          Preview payroll details
        </button>

        <div ref={submitMenuRef} style={{ display: 'flex', position: 'relative' }}>
          <button
            onClick={onSubmit}
            disabled={disabled}
            style={{
              padding: '14px 28px', background: C.brand, border: 'none',
              borderRadius: '10px 0 0 10px', color: C.card,
              fontSize: 15, fontWeight: 700,
              cursor: disabled ? 'not-allowed' : 'pointer',
              fontFamily: FONT,
              boxShadow: '0 1px 2px rgba(18,38,43,0.12)',
              opacity: disabled ? 0.45 : 1,
            }}
          >
            {finalizing ? 'Submitting...' : (savingDraft ? 'Saving...' : 'Submit payroll')}
          </button>
          <button
            onClick={() => setSubmitMenuOpen((o) => !o)}
            disabled={disabled}
            aria-label="More submit options"
            style={{
              padding: '13px 12px', background: C.brand, border: 'none',
              borderLeft: '1px solid rgba(255,255,255,0.22)',
              borderRadius: '0 10px 10px 0',
              cursor: disabled ? 'not-allowed' : 'pointer',
              boxShadow: '0 1px 2px rgba(18,38,43,0.12)',
              opacity: disabled ? 0.45 : 1,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <ChevronDown size={15} color="#FFFFFF" strokeWidth={2.5} />
          </button>

          {submitMenuOpen && (
            <div style={{
              position: 'absolute', bottom: '100%', right: 0, marginBottom: 8,
              background: C.card, border: '1px solid ' + C.line, borderRadius: 10,
              padding: 6, boxShadow: '0 6px 20px rgba(16,26,43,0.14)',
              minWidth: 170, zIndex: 200,
            }}>
              <div
                onClick={() => { setSubmitMenuOpen(false); onSaveForLater(); }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = C.tealTint;
                  (e.currentTarget as HTMLDivElement).style.color = C.tealInk;
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.background = 'transparent';
                  (e.currentTarget as HTMLDivElement).style.color = C.ink;
                }}
                style={{
                  padding: '9px 12px', fontSize: 14, borderRadius: 7,
                  color: C.ink, cursor: 'pointer', fontFamily: FONT,
                }}
              >
                Save for later
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}